/*
 * ATOM LED — Cart Sync  (assets/atom-cart-sync.js)
 * ---------------------------------------------------------------------------
 * PROBLEM
 *   Only the main product "Add to Basket" re-renders + opens the cart drawer.
 *   Items added from any OTHER source — the native "Useful add-ons" upsell
 *   (form.info-upsell-form) and the Selleasy app — land in the cart, but the
 *   drawer opens showing its stale page-load contents, so the just-added item
 *   is missing.
 *
 * SOLUTION
 *   One controller that re-renders + opens the drawer after ANY add, no matter
 *   which script triggered it. The only reliable cross-app signal is the network
 *   request to /cart/add, so we observe both fetch() and XMLHttpRequest — purely
 *   observe-only: we never alter the request or the response, we only react after
 *   a successful add. Debounced, idempotent, and fail-safe (no-ops if the theme
 *   isn't using a drawer or a fetch fails).
 *
 *   The main product form keeps its own optimised path (it piggybacks the drawer
 *   section onto its add request — a single round-trip) and calls
 *   AtomCartSync.suppress() so this observer doesn't double-handle it.
 *
 *   renderDrawerFromHTML() is the single source of truth for "repaint #CartDrawer
 *   + open" — shared by the main form and this observer.
 *
 *   Operator-confirmed 2026-06-03. Loaded only when settings.cart_type == 'drawer'.
 */
(function () {
  'use strict';

  var SECTION = 'cart-drawer';
  var DEBOUNCE_MS = 120;
  var refreshTimer = null;
  var suppressUntil = 0;

  // Capture the unwrapped fetch up front so our own section request never
  // re-enters the observer below.
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  function getDrawer() { return document.querySelector('cart-drawer'); }

  function isAddUrl(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|#|$)/.test(url);
  }

  /* Single source of truth: swap #CartDrawer for fresh server HTML, re-bind the
     overlay, then open. Uses replaceWith + importNode (not innerHTML) so the
     <cart-drawer-items> custom element re-upgrades cleanly. */
  function renderDrawerFromHTML(html) {
    var cd = getDrawer();
    if (!cd || !html) return false;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('#CartDrawer');
    var live = document.getElementById('CartDrawer');
    if (!fresh || !live) return false;
    try {
      live.replaceWith(document.importNode(fresh, true));
      var overlay = document.getElementById('CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', cd.close.bind(cd));
      cd.classList.remove('is-empty');
      if (typeof cd.open === 'function') cd.open();
      return true;
    } catch (e) {
      return false;
    }
  }

  /* Fetch the cart-drawer section for the current page via the Section Rendering
     API, then render + open. Used for adds that can't piggyback the section
     (Selleasy, native upsell, any app). */
  function refreshAndOpen() {
    if (!getDrawer() || !nativeFetch) return;
    var base = window.location.pathname || '/';
    var url = base + (base.indexOf('?') > -1 ? '&' : '?') + 'sections=' + SECTION;
    nativeFetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (data) { renderDrawerFromHTML(data && data[SECTION]); })
      .catch(function () { /* fail-safe: leave the drawer as-is */ });
  }

  function scheduleRefresh() {
    if (Date.now() < suppressUntil) return; // main form is handling this add itself
    if (!getDrawer()) return;               // not a drawer theme — nothing to do
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshAndOpen, DEBOUNCE_MS); // collapse rapid adds
  }

  /* ---- Network observers (observe-only, never mutate request/response) ---- */
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var method = (init && init.method) || (input && input.method) || 'GET';
      var promise = nativeFetch.apply(null, arguments);
      if (isAddUrl(url) && String(method).toUpperCase() === 'POST') {
        promise.then(function (res) { if (res && res.ok) scheduleRefresh(); })
               .catch(function () {});
      }
      return promise;
    };
  }

  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__atomIsAdd = String(method || '').toUpperCase() === 'POST' && isAddUrl(url);
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__atomIsAdd) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleRefresh();
      });
    }
    return xhrSend.apply(this, arguments);
  };

  /* ---- Public API --------------------------------------------------------- */
  window.AtomCartSync = {
    renderDrawerFromHTML: renderDrawerFromHTML,
    refreshAndOpen: refreshAndOpen,
    // Main form calls this before its own add so the observer doesn't double-fire.
    suppress: function (ms) { suppressUntil = Date.now() + (ms || 2000); }
  };
})();
